"use client"
import Grid from "elements/Grid";
import RightArrowIcon from "assets/imgs/right-arrow-full.svg";
import { removeHTMLFromInput } from "utils/removeHTMLFromInput";
import Link from "next/link";
import SearchHeader from "./shared/SearchHeader";
import ReadMoreCard from "./shared/ReadMoreCard";
import ContactUsSection from "./shared/ContactUsSection";
import PricingIcon from "public/file-pricing-icon-with-circle.svg";
import PropertyTaxBasicsIcon from "public/percentage-with-circle.svg";
import HomeIcon from "public/home-with-circle.svg";
import LocationIcon from "public/location-with-circle.svg";
import { IArticle, HelpCenterHome } from "utils/types";
import { sendWebhookToHelpCenter } from "utils/sendWebhook";
import { formatHelpCenterUrl } from "./shared/helpCenterUrlUtils";
import { useEventTracker_DEPRECATED } from "utils/useEventTracker";
import constants from "utils/constants";
import { useAuthContext } from "context/AuthContext";
import { useEffect } from "react";
import { useAppContext } from "context/AppContext";
import Head from "next/head";

const PEOPLE_OFTEN_SEARCH_TERMS = ["check status", "exemptions", "invoice"];

const WEBHOOK_PEOPLE_OFTEN_SEARCH_FOR =
	"Clicked 'People often searched for' term: ";

const categoryIcons = {
	"8128499980827": (
		<LocationIcon className="help-center-featured-categories-award-icon" />
	),
	"8128484305051": (
		<PropertyTaxBasicsIcon className="help-center-featured-categories-award-icon" />
	),
	"7851265071643": (
		<PricingIcon className="help-center-featured-categories-award-icon" />
	),
	default: <HomeIcon className="help-center-featured-categories-award-icon" />,
};

const HelpCenterPage = ({
	articles,
	home,
}: {
	articles: IArticle[];
	home: HelpCenterHome;
}) => {
	const { categories, promoted } = home;
	const { userData } = useAuthContext();
	const { mixpanelInitialized } = useAppContext();

	const trackVisitHelpCenter = useEventTracker_DEPRECATED(
		constants.EVENTS.PORTAL_VISIT_HELP_CENTER,
		{ userId: userData?.id }
	);

	const trackCategoryClick = useEventTracker_DEPRECATED(
		constants.EVENTS.HELP_CENTER_CATEGORY_SELECTION
	);
	const trackArticleClick = useEventTracker_DEPRECATED(
		constants.EVENTS.HELP_CENTER_ARTICLE_SELECTION
	);

	const handleArticleOnClick = (articleName: string) => {
		trackArticleClick({ "Help Center Article": articleName });
	};

	useEffect(() => {
		if (mixpanelInitialized) {
			trackVisitHelpCenter();
		}
	}, [mixpanelInitialized]);

	return (
		<div id="help-center">
			<SearchHeader header="How can we help you?" articles={articles} />
			<Grid>
				<div className="help-center-often-searched">
					<p>
						<span className="bold">People often search for: </span>
						{PEOPLE_OFTEN_SEARCH_TERMS.map((oftenSearched, index) => (
							<span
								key={oftenSearched}
								onClick={() =>
									sendWebhookToHelpCenter(
										WEBHOOK_PEOPLE_OFTEN_SEARCH_FOR + oftenSearched
									)
								}>
								<Link
									href={`/help/results?search=${encodeURIComponent(
										oftenSearched
									)}`}
									className="help-center-often-searched-links">
									{oftenSearched}
								</Link>
								{index === PEOPLE_OFTEN_SEARCH_TERMS.length - 1 ? "" : ", "}
							</span>
						))}
					</p>
				</div>
			</Grid>
			<Grid>
				<div className="help-center-featured-categories">
					{categories ? (
						categories.map(val => (
							<div
								key={val.id}
								className="help-center-featured-categories-cards-wrapper">
								<Link
									passHref
									href={`/help/category/${formatHelpCenterUrl(
										val.id,
										val.title
									)}`}>
									<div
										className="help-center-featured-categories-cards"
										onClick={() => {
											sendWebhookToHelpCenter("Clicked Category: " + val.title);
											trackCategoryClick({
												"Help Center Category": val.title,
											});
										}}>
										{categoryIcons.hasOwnProperty(val.id)
											? categoryIcons[val.id as keyof typeof categoryIcons]
											: categoryIcons["default"]}
										<div>
											<h5>{val.title}</h5>
											<p className="sm help-center-featured-categories-cards-body">
												{!val.body
													? `More information about ${val.title}`
													: val.body}
											</p>
											<div className="view-more-right-arrow-cta">
												<p className="button">View</p>
												<RightArrowIcon />
											</div>
										</div>
									</div>
								</Link>
							</div>
						))
					) : (
						<div className="text-center">
							<p>Oops! Something went wrong.</p>
							<p>Try refreshing your page or try again later.</p>
						</div>
					)}
				</div>
			</Grid>
			{promoted.length > 0 && (
				<div className="help-center-featured-articles-grid-container">
					<Grid>
						<div className="help-center-featured-articles">
							<h4>Featured Articles</h4>
							<Grid id="help-center-featured-articles-grid">
								{promoted.map(val => (
									<div key={val.id} className="help-center-articles-card-link">
										<Link
											href={`/help/article/${formatHelpCenterUrl(
												val.id,
												val.title
											)}`}
											onClick={() => handleArticleOnClick(val.title)}>
											<ReadMoreCard
												key={val.id}
												title={val.title ? val.title : ""}
												body={
													val.body
														? removeHTMLFromInput(val.body.substring(0, 180))
														: ""
												}
												onClick={() =>
													sendWebhookToHelpCenter(
														"Clicked Featured Article: " + val.title
													)
												}
											/>
										</Link>
									</div>
								))}
							</Grid>
						</div>
					</Grid>
				</div>
			)}
			<ContactUsSection mixpanelLocation="home" />
		</div>
	);
};

export default HelpCenterPage;
